import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function CitizenshipPage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('grandparent');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top when tab changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const openRelocationExpert = () => {
    const assistantButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (assistantButton) {
      assistantButton.click();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />

      {/* Hero Section */}
      <div className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/ef2e99abb8b9f526fc7ab4a6b72c25fd.jpg"
            alt="Irish Citizenship"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full py-20">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <i className="ri-passport-line text-lg"></i>
            Irish Citizenship Guide
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Claim Your Irish<br />Citizenship Today
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto">
            Discover if you're eligible for Irish citizenship through your grandparents or great-grandparents. Unlock EU citizenship and all its incredible benefits.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://irishbirthcertificates.com"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#D4AF37] text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-[#C19B2F] transition-all hover-lift whitespace-nowrap shadow-xl"
            >
              <i className="ri-file-text-line mr-2"></i>
              Get Your Irish Birth Certificate
            </a>
            <button
              onClick={openRelocationExpert}
              className="bg-white text-[#0A381F] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all hover-lift whitespace-nowrap"
            >
              <i className="ri-question-answer-line mr-2"></i>
              Ask Our Expert
            </button>
          </div>
        </div>
      </div>

      {/* Why Irish Citizenship Section */}
      <div className="py-20 bg-gradient-to-br from-[#0A381F]/5 to-emerald-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Irish Citizenship is a Game-Changer
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Irish citizenship isn't just a passport—it's your gateway to living, working, and thriving anywhere in the European Union
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              {
                icon: 'ri-earth-line',
                title: 'Live Anywhere in the EU',
                description: 'Automatic right to live, work, and study in all 27 EU countries plus Switzerland, Norway, and Iceland',
                color: '[#0A381F]',
              },
              {
                icon: 'ri-passport-line',
                title: 'Visa-Free Travel',
                description: 'Access to 188+ countries without a visa, including the USA, Canada, Australia, and Japan',
                color: '[#D4AF37]',
              },
              {
                icon: 'ri-briefcase-line',
                title: 'Career Opportunities',
                description: 'Work for any company in Europe without sponsorship. Access to EU job markets worth trillions',
                color: '[#0A381F]',
              },
              {
                icon: 'ri-heart-pulse-line',
                title: 'Healthcare Access',
                description: 'Qualify for healthcare in any EU country. Emergency care covered across Europe',
                color: '[#D4AF37]',
              },
              {
                icon: 'ri-graduation-cap-line',
                title: 'Education Benefits',
                description: 'EU tuition rates at universities. Access to world-class education at a fraction of US costs',
                color: '[#0A381F]',
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Dual Citizenship',
                description: 'Keep your US citizenship. Ireland allows dual nationality with no restrictions',
                color: '[#D4AF37]',
              },
            ].map((benefit, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all hover-lift group"
              >
                <div className={`w-16 h-16 bg-${benefit.color}/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <i className={`${benefit.icon} text-3xl text-${benefit.color}`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>

          {/* CTA Banner */}
          <div className="bg-gradient-to-r from-[#0A381F] to-[#0D4A28] rounded-2xl p-8 md:p-12 text-center">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Claim Your Irish Heritage?
            </h3>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Start your journey to Irish citizenship today. We'll help you navigate the entire process.
            </p>
            <a
              href="https://irishbirthcertificates.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#D4AF37] text-white px-10 py-5 rounded-xl text-lg font-bold hover:bg-[#C19B2F] transition-all hover-lift shadow-xl whitespace-nowrap"
            >
              <i className="ri-external-link-line text-2xl"></i>
              Visit IrishBirthCertificates.com
              <i className="ri-arrow-right-line text-xl"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Foreign Birth Registration - Grandparent Rule (MAIN FOCUS) */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 text-[#D4AF37] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-star-fill text-lg"></i>
              Most Popular Path to Irish Citizenship
            </div>
            <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              The Grandparent Rule:<br />
              <span className="bg-gradient-to-r from-[#0A381F] to-[#D4AF37] bg-clip-text text-transparent">
                Your Fast Track to Irish Citizenship
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              If you have an Irish-born grandparent, you may be eligible for Irish citizenship through Foreign Birth Registration. This is the easiest and fastest way for Americans to claim Irish citizenship.
            </p>
          </div>

          {/* Eligibility Tabs */}
          <div className="mb-12">
            <div className="flex flex-wrap gap-3 justify-center mb-8">
              <button
                onClick={() => setActiveTab('grandparent')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  activeTab === 'grandparent'
                    ? 'bg-[#0A381F] text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="ri-parent-line mr-2"></i>
                Grandparent Rule
              </button>
              <button
                onClick={() => setActiveTab('parent')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  activeTab === 'parent'
                    ? 'bg-[#0A381F] text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="ri-user-heart-line mr-2"></i>
                Parent Born in Ireland
              </button>
              <button
                onClick={() => setActiveTab('descent')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  activeTab === 'descent'
                    ? 'bg-[#0A381F] text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="ri-team-line mr-2"></i>
                Irish Descent
              </button>
              <button
                onClick={() => setActiveTab('naturalization')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  activeTab === 'naturalization'
                    ? 'bg-[#0A381F] text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="ri-time-line mr-2"></i>
                Naturalization
              </button>
            </div>

            {/* Grandparent Rule Content */}
            {activeTab === 'grandparent' && (
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 md:p-12 border-2 border-[#D4AF37]">
                <div className="max-w-4xl mx-auto">
                  <div className="flex items-start gap-4 mb-8">
                    <div className="w-16 h-16 bg-[#D4AF37] rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-star-fill text-3xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-gray-900 mb-3">
                        Foreign Birth Registration (FBR) - The Grandparent Rule
                      </h3>
                      <p className="text-lg text-gray-700">
                        This is the most common and straightforward path for Americans with Irish heritage. If your grandparent was born in Ireland, you can register as an Irish citizen.
                      </p>
                    </div>
                  </div>

                  {/* Eligibility Requirements */}
                  <div className="bg-white rounded-xl p-6 md:p-8 mb-8 shadow-lg">
                    <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <i className="ri-checkbox-circle-line text-[#0A381F] text-3xl"></i>
                      You're Eligible If:
                    </h4>
                    <div className="space-y-4">
                      {[
                        'Your grandparent was born on the island of Ireland (Republic of Ireland or Northern Ireland)',
                        'Your parent was registered in the Foreign Births Register BEFORE you were born',
                        'OR you were born before January 1, 2005 (automatic eligibility)',
                        'You can provide documentation proving the family connection',
                      ].map((requirement, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <i className="ri-check-double-line text-2xl text-[#0A381F] flex-shrink-0 mt-1"></i>
                          <p className="text-lg text-gray-700">{requirement}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Required Documents */}
                  <div className="bg-white rounded-xl p-6 md:p-8 mb-8 shadow-lg">
                    <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <i className="ri-file-list-3-line text-[#D4AF37] text-3xl"></i>
                      Documents You'll Need:
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {[
                        'Your birth certificate (long form)',
                        'Your parent\'s birth certificate',
                        'Your grandparent\'s Irish birth certificate',
                        'Your parent\'s marriage certificate',
                        'Your grandparent\'s marriage certificate',
                        'Proof of current address',
                        'Valid passport photos',
                        'Completed FBR application form',
                      ].map((doc, index) => (
                        <div key={index} className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg">
                          <i className="ri-file-text-line text-xl text-[#0A381F]"></i>
                          <span className="text-gray-700">{doc}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Process Timeline */}
                  <div className="bg-white rounded-xl p-6 md:p-8 mb-8 shadow-lg">
                    <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <i className="ri-time-line text-[#0A381F] text-3xl"></i>
                      Application Timeline:
                    </h4>
                    <div className="space-y-6">
                      {[
                        { step: '1', title: 'Gather Documents', time: '2-4 weeks', description: 'Collect all required birth certificates and documents' },
                        { step: '2', title: 'Submit Application', time: '1 day', description: 'Complete online application and pay €278 fee' },
                        { step: '3', title: 'Processing', time: '12-18 months', description: 'Department of Foreign Affairs reviews your application' },
                        { step: '4', title: 'Receive Certificate', time: '2-4 weeks', description: 'Get your Foreign Birth Registration certificate' },
                        { step: '5', title: 'Apply for Passport', time: '4-6 weeks', description: 'Apply for your Irish passport' },
                      ].map((phase, index) => (
                        <div key={index} className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-[#0A381F] text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                            {phase.step}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="text-xl font-bold text-gray-900">{phase.title}</h5>
                              <span className="text-sm font-semibold text-[#D4AF37] bg-[#D4AF37]/10 px-3 py-1 rounded-full whitespace-nowrap">
                                {phase.time}
                              </span>
                            </div>
                            <p className="text-gray-600">{phase.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Cost Breakdown */}
                  <div className="bg-white rounded-xl p-6 md:p-8 mb-8 shadow-lg">
                    <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <i className="ri-money-euro-circle-line text-[#0A381F] text-3xl"></i>
                      Total Cost Estimate:
                    </h4>
                    <div className="space-y-3">
                      {[
                        { item: 'Foreign Birth Registration Fee', cost: '€278' },
                        { item: 'Irish Birth Certificates (3-4 needed)', cost: '$150-200' },
                        { item: 'US Birth Certificates', cost: '$50-100' },
                        { item: 'Document Authentication/Apostille', cost: '$100-200' },
                        { item: 'Passport Application Fee', cost: '€95' },
                        { item: 'Professional Service (Optional)', cost: '$500-1,500' },
                      ].map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <span className="text-gray-700">{item.item}</span>
                          <span className="font-bold text-[#0A381F]">{item.cost}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between p-4 bg-[#0A381F] text-white rounded-lg font-bold text-lg">
                        <span>Total Estimated Cost:</span>
                        <span>$1,200 - $2,500</span>
                      </div>
                    </div>
                  </div>

                  {/* Big CTA */}
                  <div className="bg-gradient-to-r from-[#D4AF37] to-[#C19B2F] rounded-xl p-8 text-center">
                    <h4 className="text-3xl font-bold text-white mb-4">
                      Need Your Irish Birth Certificates?
                    </h4>
                    <p className="text-xl text-white/90 mb-6">
                      We make it easy to obtain official Irish birth certificates for your citizenship application
                    </p>
                    <a
                      href="https://irishbirthcertificates.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-3 bg-white text-[#0A381F] px-10 py-5 rounded-xl text-lg font-bold hover:bg-gray-50 transition-all hover-lift shadow-xl whitespace-nowrap"
                    >
                      <i className="ri-file-text-line text-2xl"></i>
                      Order Irish Birth Certificates Now
                      <i className="ri-arrow-right-line text-xl"></i>
                    </a>
                    <p className="text-white/80 text-sm mt-4">
                      Fast, reliable service • Official certificates • Expert support
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Parent Born in Ireland Content */}
            {activeTab === 'parent' && (
              <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
                <div className="max-w-4xl mx-auto">
                  <h3 className="text-3xl font-bold text-gray-900 mb-6">
                    Automatic Citizenship Through Irish-Born Parent
                  </h3>
                  <p className="text-lg text-gray-600 mb-8">
                    If one of your parents was born in Ireland, you are automatically an Irish citizen by birth, regardless of where you were born.
                  </p>

                  <div className="bg-white rounded-xl p-6 md:p-8 mb-8 shadow-lg">
                    <h4 className="text-2xl font-bold text-gray-900 mb-6">You're Automatically Eligible If:</h4>
                    <div className="space-y-4">
                      {[
                        'Your mother or father was born in Ireland (Republic or Northern Ireland)',
                        'Your parent was an Irish citizen at the time of your birth',
                        'You were born anywhere in the world',
                      ].map((requirement, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <i className="ri-check-double-line text-2xl text-[#0A381F] flex-shrink-0 mt-1"></i>
                          <p className="text-lg text-gray-700">{requirement}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 md:p-8 mb-8 shadow-lg">
                    <h4 className="text-2xl font-bold text-gray-900 mb-6">What You Need to Do:</h4>
                    <div className="space-y-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#0A381F] text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">1</div>
                        <div>
                          <h5 className="text-xl font-bold text-gray-900 mb-2">Gather Documents</h5>
                          <p className="text-gray-600">Your birth certificate, parent's Irish birth certificate, and parent's marriage certificate</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#0A381F] text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">2</div>
                        <div>
                          <h5 className="text-xl font-bold text-gray-900 mb-2">Apply for Passport</h5>
                          <p className="text-gray-600">You can apply directly for an Irish passport without registering first</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#0A381F] text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">3</div>
                        <div>
                          <h5 className="text-xl font-bold text-gray-900 mb-2">Receive Passport</h5>
                          <p className="text-gray-600">Processing takes 4-6 weeks for standard applications</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-[#0A381F] to-[#0D4A28] rounded-xl p-8 text-center">
                    <h4 className="text-2xl font-bold text-white mb-4">Need Your Parent's Irish Birth Certificate?</h4>
                    <a
                      href="https://irishbirthcertificates.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-3 bg-[#D4AF37] text-white px-8 py-4 rounded-xl text-lg font-bold hover:bg-[#C19B2F] transition-all hover-lift shadow-xl whitespace-nowrap"
                    >
                      Order Now at IrishBirthCertificates.com
                      <i className="ri-arrow-right-line text-xl"></i>
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Irish Descent Content */}
            {activeTab === 'descent' && (
              <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
                <div className="max-w-4xl mx-auto">
                  <h3 className="text-3xl font-bold text-gray-900 mb-6">
                    Citizenship by Irish Descent (Great-Grandparents)
                  </h3>
                  <p className="text-lg text-gray-600 mb-8">
                    If your great-grandparent was born in Ireland, you may still be eligible for citizenship, but the process is more complex.
                  </p>

                  <div className="bg-white rounded-xl p-6 md:p-8 mb-8 shadow-lg">
                    <h4 className="text-2xl font-bold text-gray-900 mb-6">Requirements:</h4>
                    <div className="space-y-4">
                      {[
                        'Your great-grandparent was born in Ireland',
                        'Your grandparent was registered in the Foreign Births Register',
                        'Your parent was registered in the Foreign Births Register BEFORE you were born',
                        'You must prove continuous connection to Ireland (residency, cultural ties)',
                      ].map((requirement, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <i className="ri-arrow-right-circle-line text-2xl text-[#0A381F] flex-shrink-0 mt-1"></i>
                          <p className="text-lg text-gray-700">{requirement}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 mb-8">
                    <div className="flex items-start gap-4">
                      <i className="ri-information-line text-3xl text-amber-600 flex-shrink-0"></i>
                      <div>
                        <h5 className="text-xl font-bold text-gray-900 mb-2">Important Note:</h5>
                        <p className="text-gray-700">
                          This path is significantly more difficult and requires each generation to have been registered before the next was born. Most Americans will not qualify through great-grandparents unless their family maintained continuous registration.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-[#0A381F] to-[#0D4A28] rounded-xl p-8 text-center">
                    <h4 className="text-2xl font-bold text-white mb-4">Not Sure If You Qualify?</h4>
                    <button
                      onClick={openRelocationExpert}
                      className="inline-flex items-center gap-3 bg-white text-[#0A381F] px-8 py-4 rounded-xl text-lg font-bold hover:bg-gray-50 transition-all hover-lift shadow-xl whitespace-nowrap"
                    >
                      <i className="ri-question-answer-line text-2xl"></i>
                      Ask Our Relocation Expert
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Naturalization Content */}
            {activeTab === 'naturalization' && (
              <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
                <div className="max-w-4xl mx-auto">
                  <h3 className="text-3xl font-bold text-gray-900 mb-6">
                    Citizenship by Naturalization (Residency)
                  </h3>
                  <p className="text-lg text-gray-600 mb-8">
                    If you don't have Irish ancestry, you can become an Irish citizen by living in Ireland for a certain period of time.
                  </p>

                  <div className="bg-white rounded-xl p-6 md:p-8 mb-8 shadow-lg">
                    <h4 className="text-2xl font-bold text-gray-900 mb-6">Standard Requirements:</h4>
                    <div className="space-y-4">
                      {[
                        'Live in Ireland for 5 out of the last 9 years',
                        'Including 1 continuous year immediately before application',
                        'Have valid residency permission during this time',
                        'Be of good character (no criminal record)',
                        'Intend to continue living in Ireland',
                        'Pass a citizenship ceremony',
                      ].map((requirement, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <i className="ri-check-line text-2xl text-[#0A381F] flex-shrink-0 mt-1"></i>
                          <p className="text-lg text-gray-700">{requirement}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 md:p-8 mb-8 shadow-lg">
                    <h4 className="text-2xl font-bold text-gray-900 mb-6">Faster Track (3 Years):</h4>
                    <p className="text-gray-600 mb-4">You may qualify for citizenship after just 3 years if you are:</p>
                    <div className="space-y-3">
                      {[
                        'Married to or in a civil partnership with an Irish citizen',
                        'A refugee or stateless person',
                        'Born in Ireland but not entitled to citizenship at birth',
                      ].map((option, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <i className="ri-star-line text-xl text-[#D4AF37] flex-shrink-0 mt-1"></i>
                          <p className="text-gray-700">{option}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 md:p-8 mb-8 shadow-lg">
                    <h4 className="text-2xl font-bold text-gray-900 mb-6">Application Process:</h4>
                    <div className="space-y-6">
                      {[
                        { step: 'Application Fee', detail: '€175 (non-refundable)' },
                        { step: 'Processing Time', detail: '12-24 months' },
                        { step: 'Citizenship Ceremony Fee', detail: '€950 (if approved)' },
                        { step: 'Certificate of Naturalization', detail: 'Issued after ceremony' },
                      ].map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <span className="font-semibold text-gray-900">{item.step}</span>
                          <span className="text-[#0A381F] font-bold">{item.detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-[#0A381F] to-[#0D4A28] rounded-xl p-8 text-center">
                    <h4 className="text-2xl font-bold text-white mb-4">Planning to Move to Ireland?</h4>
                    <a
                      href="/directory"
                      className="inline-flex items-center gap-3 bg-[#D4AF37] text-white px-8 py-4 rounded-xl text-lg font-bold hover:bg-[#C19B2F] transition-all hover-lift shadow-xl whitespace-nowrap"
                    >
                      <i className="ri-compass-3-line text-2xl"></i>
                      Find Immigration Services
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Irish Birth Certificates Promotion Section */}
      <div className="py-20 bg-gradient-to-br from-[#D4AF37]/10 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Left Side - Image */}
              <div className="relative h-64 md:h-auto">
                <img
                  src="https://readdy.ai/api/search-image?query=official%20irish%20birth%20certificate%20document%20with%20celtic%20harp%20emblem%20government%20seal%20authentic%20legal%20documentation%20professional%20registry%20office%20paperwork%20heritage%20ancestry%20family%20history%20records%20genealogy%20research&width=800&height=600&seq=birth-cert-promo-001&orientation=portrait"
                  alt="Irish Birth Certificate"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>

              {/* Right Side - Content */}
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 text-[#D4AF37] px-4 py-2 rounded-full text-sm font-semibold mb-6 self-start">
                  <i className="ri-award-line text-lg"></i>
                  Official Service Partner
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Get Your Irish Birth Certificates Fast & Easy
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  We specialize in obtaining official Irish birth certificates for citizenship applications. Fast, reliable, and hassle-free service.
                </p>

                <div className="space-y-4 mb-8">
                  {[
                    { icon: 'ri-shield-check-line', text: 'Official certificates from Irish General Register Office' },
                    { icon: 'ri-time-line', text: 'Fast processing - typically 2-4 weeks' },
                    { icon: 'ri-customer-service-2-line', text: 'Expert support throughout the process' },
                    { icon: 'ri-secure-payment-line', text: 'Secure online ordering and payment' },
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#0A381F]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className={`${feature.icon} text-xl text-[#0A381F]`}></i>
                      </div>
                      <span className="text-gray-700">{feature.text}</span>
                    </div>
                  ))}
                </div>

                <a
                  href="https://irishbirthcertificates.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-[#D4AF37] to-[#C19B2F] text-white px-8 py-5 rounded-xl text-lg font-bold hover:shadow-xl transition-all hover-lift whitespace-nowrap"
                >
                  <i className="ri-external-link-line text-2xl"></i>
                  Visit IrishBirthCertificates.com
                  <i className="ri-arrow-right-line text-xl"></i>
                </a>

                <p className="text-sm text-gray-500 mt-4">
                  Trusted by thousands of Americans claiming Irish citizenship
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about Irish citizenship
            </p>
          </div>

          <div className="space-y-6">
            {[
              {
                question: 'How long does the Foreign Birth Registration process take?',
                answer: 'The current processing time is approximately 12-18 months from the date of application. However, gathering all required documents can take 2-4 weeks before you can submit your application.',
              },
              {
                question: 'Can I keep my US citizenship if I become an Irish citizen?',
                answer: 'Yes! Ireland allows dual citizenship, and the United States does not require you to renounce your US citizenship when you acquire Irish citizenship. You can hold both passports.',
              },
              {
                question: 'Do I need to speak Irish (Gaelic) to become a citizen?',
                answer: 'No, there is no language requirement for Irish citizenship through descent or Foreign Birth Registration. English is an official language of Ireland.',
              },
              {
                question: 'What if my grandparent was born in Northern Ireland?',
                answer: 'People born in Northern Ireland are entitled to Irish citizenship, so if your grandparent was born anywhere on the island of Ireland (including Northern Ireland), you may be eligible.',
              },
              {
                question: 'How much does it cost to get Irish citizenship?',
                answer: 'The Foreign Birth Registration fee is €278. You\'ll also need to pay for birth certificates ($150-200), document authentication ($100-200), and eventually a passport (€95). Total cost is typically $1,200-2,500.',
              },
              {
                question: 'Can my children become Irish citizens too?',
                answer: 'Yes! Once you are registered as an Irish citizen, your children can also claim citizenship through you. However, you must be registered BEFORE they are born for automatic eligibility.',
              },
              {
                question: 'What\'s the difference between citizenship and residency?',
                answer: 'Citizenship gives you an Irish passport and full rights as an EU citizen. Residency just allows you to live in Ireland temporarily. Citizenship is permanent and can be passed to your children.',
              },
              {
                question: 'Do I need to live in Ireland to maintain citizenship?',
                answer: 'No! Once you have Irish citizenship, it\'s yours for life. There\'s no residency requirement to maintain it, and you can live anywhere in the world.',
              },
            ].map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all">
                <h4 className="text-xl font-bold text-gray-900 mb-3 flex items-start gap-3">
                  <i className="ri-question-line text-2xl text-[#D4AF37] flex-shrink-0"></i>
                  {faq.question}
                </h4>
                <p className="text-gray-600 ml-11">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=dublin%20ireland%20city%20skyline%20modern%20architecture%20liffey%20river%20ha%20penny%20bridge%20historic%20buildings%20vibrant%20urban%20landscape%20irish%20capital%20beautiful%20cityscape%20sunset%20golden%20hour&width=1920&height=800&seq=final-cta-dublin-001&orientation=landscape"
            alt="Dublin Ireland"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A381F]/90 to-[#0D4A28]/90"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Start Your Irish Citizenship Journey Today
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Don't wait to claim your Irish heritage. Begin the process now and unlock all the benefits of EU citizenship.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://irishbirthcertificates.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-white px-10 py-5 rounded-xl text-lg font-bold hover:bg-[#C19B2F] transition-all hover-lift shadow-xl whitespace-nowrap"
            >
              <i className="ri-file-text-line text-2xl"></i>
              Get Birth Certificates
            </a>
            <button
              onClick={openRelocationExpert}
              className="inline-flex items-center justify-center gap-3 bg-white text-[#0A381F] px-10 py-5 rounded-xl text-lg font-bold hover:bg-gray-50 transition-all hover-lift shadow-xl whitespace-nowrap"
            >
              <i className="ri-question-answer-line text-2xl"></i>
              Ask Our Expert
            </button>
          </div>
          <p className="text-white/70 text-sm mt-6">
            Join thousands of Americans who have successfully claimed Irish citizenship
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
